import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Trophy, Home, RotateCcw, Volume2, VolumeX, ArrowLeft, User, Gift, Loader2 } from 'lucide-react';
import { useGame } from '../../context/GameContext';
import { useRewards } from '../../context/RewardsContext';
import { PointsButton } from '../../components/RewardsWidgets';
import { getMemoryCards } from '../../utils/mockData';
import soundEffects from '../../utils/soundEffects';
import CelebrationAnimation from '../../components/CelebrationAnimation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MemoryGame = () => {
  const navigate = useNavigate();
  const { selectedAge, selectedSubject, addScore } = useGame();
  const { awardCorrectAnswer, awardLessonComplete } = useRewards();
  const [allRounds, setAllRounds] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [score, setScore] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showRoundComplete, setShowRoundComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);

  // Get the best available voice
  const getBestVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return null;
    
    const preferredVoices = [
      'Samantha', 'Google US English', 'Google UK English Female',
      'Microsoft Zira', 'Microsoft David', 'Karen', 'Moira', 'Fiona',
      'Victoria', 'Alex', 'Daniel', 'Tessa',
    ];
    
    for (const preferred of preferredVoices) {
      const voice = voices.find(v => v.name.includes(preferred));
      if (voice) return voice;
    }
    
    const englishVoice = voices.find(v => 
      v.lang.startsWith('en') && 
      (v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Natural') || !v.name.includes('eSpeak'))
    );
    if (englishVoice) return englishVoice;
    
    return voices.find(v => v.lang.startsWith('en')) || voices[0];
  }, []);

  // Load voices when available
  useEffect(() => {
    const loadVoices = () => {
      const voice = getBestVoice();
      if (voice) setSelectedVoice(voice);
    };
    
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [getBestVoice]);

  // Read aloud function with better voice
  const speakText = useCallback((text) => {
    if (!selectedVoice) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.rate = 0.85;
    utterance.pitch = 1.1;
    utterance.volume = 1;
    
    setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  }, [selectedVoice]);

  // Cancel speech on unmount
  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  // Map age groups to API format
  const getAgeGroupForAPI = (age) => {
    const ageMap = {
      'preschool': 'preschool',
      '3-5': 'preschool',
      'elementary': 'elementary',
      '6-8': 'elementary',
      'advanced': 'advanced',
      '9-12': 'advanced'
    };
    return ageMap[age] || 'elementary';
  };

  // Load memory cards - try API first, fallback to mockData
  useEffect(() => {
    const loadMemoryContent = async () => {
      setLoading(true);
      try {
        const ageGroup = getAgeGroupForAPI(selectedAge);
        const response = await axios.get(`${API}/content/memory/${ageGroup}/${selectedSubject}`);
        
        if (response.data.rounds && response.data.rounds.length > 0) {
          // API data available - extract cards from rounds
          const rounds = response.data.rounds.map(r => r.cards);
          setAllRounds(rounds);
          initializeRound(rounds[0]);
          console.log(`Loaded ${rounds.length} memory rounds from API for ${ageGroup}/${selectedSubject}`);
        } else {
          // No API data - fallback to mockData
          console.log('No API data for memory, falling back to mockData');
          loadFromMockData();
        }
      } catch (error) {
        console.log('API error for memory, falling back to mockData:', error.message);
        loadFromMockData();
      }
      setLoading(false);
    };

    const loadFromMockData = () => {
      const rounds = getMemoryCards(selectedAge, selectedSubject);
      // Ensure rounds is an array of arrays
      const roundsArray = Array.isArray(rounds[0]) ? rounds : [rounds];
      setAllRounds(roundsArray);
      initializeRound(roundsArray[0]);
    };

    loadMemoryContent();
  }, [selectedAge, selectedSubject]);

  const initializeRound = (roundCards) => {
    if (!roundCards) return;
    const shuffled = [...roundCards, ...roundCards]
      .sort(() => Math.random() - 0.5)
      .map((card, index) => ({ ...card, id: index }));
    setCards(shuffled);
    setFlippedCards([]);
    setMatchedCards([]);
    setMoves(0);
    setScore(0);
  };

  const handleNextRound = () => {
    setShowRoundComplete(false);
    const nextRound = currentRound + 1;
    setCurrentRound(nextRound);
    initializeRound(allRounds[nextRound]);
  };

  const handleCardClick = (clickedCard) => {
    if (flippedCards.length === 2 || 
        flippedCards.some(card => card.id === clickedCard.id) ||
        matchedCards.includes(clickedCard.value)) {
      return;
    }

    // Play card flip sound
    if (soundEnabled) soundEffects.playReveal();

    const newFlipped = [...flippedCards, clickedCard];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(moves + 1);
      
      if (newFlipped[0].value === newFlipped[1].value) {
        setMatchedCards([...matchedCards, newFlipped[0].value]);
        const points = 20;
        setScore(score + points);
        setTotalScore(totalScore + points);
        addScore(points);
        // Award +2 points for correct match
        awardCorrectAnswer();
        // Play match sound
        if (soundEnabled) soundEffects.playMatch();
        setTimeout(() => setFlippedCards([]), 500);
        
        // Check if round is complete
        if (matchedCards.length + 1 === cards.length / 2) {
          // Award lesson completion (+10 points)
          awardLessonComplete('memory');
          // Play celebration sound
          if (soundEnabled) soundEffects.playCelebration();
          setTimeout(() => {
            if (currentRound < allRounds.length - 1) {
              // More rounds available
              setShowRoundComplete(true);
            } else {
              // All rounds complete - show celebration!
              setShowCelebration(true);
            }
          }, 1000);
        }
      } else {
        setTimeout(() => setFlippedCards([]), 1000);
      }
    }
  };

  const isFlipped = (card) => {
    return flippedCards.some(c => c.id === card.id) || matchedCards.includes(card.value);
  };

  const resetCurrentRound = () => {
    initializeRound(allRounds[currentRound]);
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    navigate('/result', {
      state: {
        score: totalScore,
        totalQuestions: allRounds.length,
        correctAnswers: allRounds.length,
        gameType: 'memory'
      }
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
      }}>
        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl text-center max-w-md" style={{ borderRadius: '28px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-purple-600 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-800">Loading Memory Game...</h2>
        </Card>
      </div>
    );
  }

  // Round complete modal
  if (showRoundComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
      }}>
        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl text-center max-w-md" style={{ borderRadius: '28px' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Round Complete!</h2>
          <p className="text-xl text-gray-600 mb-2">
            Round {currentRound + 1} of {allRounds.length} finished!
          </p>
          <p className="text-lg text-gray-600 mb-6">
            Moves: {moves} | Score: {score}
          </p>
          <Button
            onClick={handleNextRound}
            className="w-full py-6 text-xl font-bold text-white"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px'
            }}
          >
            Next Round →
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)'
    }}>
      {/* Celebration Animation */}
      <CelebrationAnimation 
        show={showCelebration} 
        onComplete={handleCelebrationComplete}
        message="Memory Master!"
        duration={3500}
      />

      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/game-format')}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-end">
            <PointsButton />
            <Button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <User className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => navigate('/rewards')}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <Gift className="w-4 h-4" />
            </Button>
            <Card className="px-3 py-2 bg-white/95" style={{ borderRadius: '12px' }}>
              <span className="font-bold text-sm md:text-lg">Round {currentRound + 1}/{allRounds.length}</span>
            </Card>
            <Card className="px-3 py-2 bg-white/95" style={{ borderRadius: '12px' }}>
              <Trophy className="w-5 h-5 inline mr-1 text-yellow-600" />
              <span className="font-bold text-lg">{totalScore}</span>
            </Card>
            <Card className="px-3 py-2 bg-white/95" style={{ borderRadius: '12px' }}>
              <span className="font-bold text-sm md:text-lg">Moves: {moves}</span>
            </Card>
            <Button
              onClick={resetCurrentRound}
              variant="outline"
              className="font-semibold"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Reset
            </Button>
          </div>
        </div>

        <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl" style={{ borderRadius: '28px' }}>
          <div className="flex items-center justify-center gap-3 mb-8">
            <h2 className="text-4xl font-bold text-gray-800 text-center">
              Find All the Matching Pairs!
            </h2>
            <button
              onClick={() => speakText("Find all the matching pairs! Click on two cards to flip them. If they match, they stay open!")}
              className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                isSpeaking 
                  ? 'bg-purple-500 text-white animate-pulse' 
                  : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
              }`}
              title="Read instructions aloud"
            >
              <Volume2 className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
            {cards.map((card) => (
              <div
                key={card.id}
                onClick={() => handleCardClick(card)}
                className="cursor-pointer transition-all duration-300 hover:scale-105"
              >
                <Card
                  className="aspect-square flex items-center justify-center text-4xl font-bold"
                  style={{
                    borderRadius: '16px',
                    background: isFlipped(card) 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    transform: isFlipped(card) ? 'rotateY(0deg)' : 'rotateY(0deg)',
                    minHeight: '80px'
                  }}
                >
                  {isFlipped(card) ? (
                    <span className="text-white">{card.display}</span>
                  ) : (
                    <span className="text-white text-6xl">?</span>
                  )}
                </Card>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-2xl font-bold text-gray-700">
              Matched: {matchedCards.length} / {cards.length / 2}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MemoryGame;
